"use client";

import {
  Command,
  CommandInput,
} from "@/components/ui/command";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";

export function SearchBar({
  collapsedOnMobile = false,
  heroStyle = false,
}: {
  collapsedOnMobile?: boolean;
  heroStyle?: boolean;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isExpanded, setIsExpanded] = useState(!collapsedOnMobile);
  const router = useRouter();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!collapsedOnMobile) {
      setIsExpanded(true);
    }
  }, [collapsedOnMobile]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && searchTerm.trim()) {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Navigate to search page with query immediately
      router.push(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If search term is not empty, set a timeout
    if (searchTerm.trim()) {
      timeoutRef.current = setTimeout(() => {
        router.push(`/search?q=${encodeURIComponent(searchTerm)}`);
      }, 800);
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, router]);

  if (collapsedOnMobile) {
    return (
      <>
        <AnimatePresence initial={false}>
          {!isExpanded ? (
            <motion.div
              key="search-trigger"
              className="relative"
              initial={{ opacity: 0.9, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <Button
                type="button"
                variant={heroStyle ? "ghost" : "outline"}
                size="icon"
                className={
                  heroStyle
                    ? "h-10 w-10 rounded-full border border-white/35 bg-black/55 backdrop-blur-md text-white hover:bg-black/70 hover:text-white"
                    : "h-10 w-10 rounded-full"
                }
                onClick={() => setIsExpanded(true)}
                aria-label="Open search"
              >
                <Search className="h-4 w-4" />
              </Button>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <AnimatePresence>
          {isExpanded ? (
            <motion.div
              key="search-panel"
              initial={{ opacity: 0, y: -10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.98 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="fixed left-6 right-6 z-50"
              style={{ top: "4.5rem" }}
            >
              <Command
                className={
                  heroStyle
                    ? "rounded-full bg-black/55 backdrop-blur-md shadow-sm h-min border border-white/35 focus-within:ring-2 focus-within:ring-white/70 focus-within:ring-offset-0 transition duration-200 ease-in-out [&_[cmdk-input-wrapper]_svg]:text-white [&_[cmdk-input-wrapper]_svg]:opacity-100"
                    : "rounded-full bg-background/75 backdrop-blur-md shadow-sm h-min border focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background transition duration-200 ease-in-out"
                }
                loop
              >
                <CommandInput
                  className={
                    heroStyle
                      ? "h-10 text-white placeholder:text-white/70"
                      : "h-10"
                  }
                  placeholder="Search bites..."
                  onValueChange={(value) => {
                    setSearchTerm(value);
                  }}
                  value={searchTerm}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  onBlur={() => {
                    if (!searchTerm.trim()) {
                      setIsExpanded(false);
                    }
                  }}
                />
              </Command>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </>
    );
  }

  return (
    <div className="w-full">
      <Command
        className={
          heroStyle
            ? "rounded-full bg-black/55 backdrop-blur-md shadow-sm h-min border border-white/35 z-50 focus-within:ring-2 focus-within:ring-white/70 focus-within:ring-offset-0 transition duration-200 ease-in-out [&_[cmdk-input-wrapper]_svg]:text-white [&_[cmdk-input-wrapper]_svg]:opacity-100"
            : "rounded-full bg-background/75 shadow-sm h-min border z-50 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background transition duration-200 ease-in-out"
        }
        loop
      >
        <CommandInput
          className={
            heroStyle
              ? "h-10 text-white placeholder:text-white/70"
              : ""
          }
          placeholder="Search bites..."
          onValueChange={(value) => {
            setSearchTerm(value);
          }}
          value={searchTerm}
          onKeyDown={handleKeyDown}
          autoFocus={collapsedOnMobile}
          onBlur={() => {
            if (collapsedOnMobile && !searchTerm.trim()) {
              setIsExpanded(false);
            }
          }}
        />
      </Command>
    </div>
  );
}
